import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Modal,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { useAuth } from "shared/providers/auth";
import { useParams } from "react-router-dom";
import { editNotes, excludeNotes, getNotes, saveNotes } from "requests/Notes";
import { MdEdit, MdDeleteForever } from "react-icons/md";

import toast from "react-hot-toast";

interface IObservation {
  NR_SEQ_OBSERVACAO_OEOC: number;
  DS_OBSERVACAO_OEOC: string;
  DT_ALTERACAO_OEOC: string;
  DT_CADASTRO_OEOC: string;
}

interface IProps {
  idPacientParam?: number;
  onClick?: () => void;
}

const NotesUser: React.FC<IProps> = ({ onClick, idPacientParam }) => {
  const [observations, setObservations] = React.useState<IObservation[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [textArea, setTextArea] = React.useState<string>("");

  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] =
    React.useState<boolean>(false);
  const [selectedObservation, setSelectedObservation] =
    React.useState<IObservation | null>(null);
  const [editText, setEditText] = React.useState<string>("");

  const { user } = useAuth();
  const { idPacient } = useParams();

  const pacId = idPacientParam && idPacientParam > 0 ? idPacientParam : Number(idPacient);

  const getObservations = React.useCallback(async () => {
    if (!pacId || pacId <= 0) return;
    setLoading(true);
    try {
      const { status, data } = await getNotes({
        userId: user.data.id,
        paciente_id: pacId,
      });

      if ([200, 201].includes(status)) {
        setObservations(data.data);
      } else {
        toast.error("Erro ao carregar as anotações.");
      }
    } catch (e: any) {
      toast.error("Erro ao carregar.");
    }
    setLoading(false);
  }, [user.data.id, pacId]);

  const formatDateTime = (dateTimeString: string): string => {
    const dateTime: Date = new Date(dateTimeString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    };

    const formattedDateTime: string = dateTime.toLocaleDateString(
      "pt-BR",
      options
    );

    const today: Date = new Date();

    // Verifica se a data é hoje
    if (dateTime.toDateString() === today.toDateString()) {
      return `${formattedDateTime.split("às")[1]}`;
    }

    // Para outras datas
    else {
      const date = new Date(dateTime);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}`;
    }
  };

  React.useEffect(() => {
    getObservations();
  }, [getObservations]);

  const handleSubmit = async () => {
    if (!textArea.trim()) return toast.error("Preencha a anotação.");
    if (!pacId || pacId <= 0) return toast.error("ID do paciente não encontrado.");

    setLoading(true);
    try {
      const { status } = await saveNotes(user.data.id, {
        observacao: textArea,
        paciente_id: pacId,
      });

      if ([200, 201].includes(status)) {
        toast.success("Anotação adicionada!");
        setTextArea("");
        await getObservations();
      } else {
        toast.error("Erro ao salvar anotação.");
      }
    } catch (e: any) {
      toast.error("Erro ao salvar.");
    }
    setLoading(false);
  };

  const handleEdit = async () => {
    try {
      const { status } = await editNotes(
        user.data.id,
        Number(selectedObservation?.NR_SEQ_OBSERVACAO_OEOC),
        {
          observacao: editText,
          paciente_id: pacId,
        }
      );

      if ([200, 201].includes(status)) {
        toast.success("Atualizado com sucesso!");
        setEditModalOpen(false);
        await getObservations();
      } else {
        toast.error("Erro ao atualizar anotação");
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleDelete = async () => {
    try {
      const { status } = await excludeNotes(
        user.data.id,
        Number(selectedObservation?.NR_SEQ_OBSERVACAO_OEOC)
      );

      if ([200, 201].includes(status)) {
        toast.success("Excluido com sucesso!");
        setDeleteDialogOpen(false);
        await getObservations();
      } else {
        toast.error("Erro ao excluir anotação");
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Box sx={{ backgroundColor: "white", height: "100%", width: "100%" }}>
      <Container sx={{ paddingBottom: 2 }}>
        <ReturnButton
          title="Anotações para Conexão com paciente"
          onClick={onClick}
        />

        {/* Campo para nova anotação */}
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <FormControl fullWidth>
            <FormLabel>Adicionar nova anotação</FormLabel>
            <TextField
              multiline
              rows={4}
              value={textArea}
              onChange={(e) => setTextArea(e.target.value)}
            />
          </FormControl>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ mt: 1, width: 150 }}
            >
              {loading ? <CircularProgress size={24} /> : "Salvar"}
            </Button>
          </Box>
        </Box>

        {/* Lista de observações */}
        <Box display="flex" flexDirection="column" gap={2} mt={4}>
          {observations.map((obs) => (
            <Paper
              key={obs.NR_SEQ_OBSERVACAO_OEOC}
              sx={{ p: 2, position: "relative" }}
            >
              <Typography
                sx={{
                  maxWidth: "80%",
                }}
              >
                {obs.DS_OBSERVACAO_OEOC}
              </Typography>
              <Box
                position="absolute"
                top={8}
                right={8}
                display="flex"
                flexDirection="row"
                alignItems="center"
              >
                <Typography>
                  {obs.DT_ALTERACAO_OEOC
                    ? formatDateTime(obs.DT_ALTERACAO_OEOC)
                    : formatDateTime(obs.DT_CADASTRO_OEOC)}
                </Typography>

                <IconButton
                  onClick={() => {
                    setSelectedObservation(obs);
                    setEditText(obs.DS_OBSERVACAO_OEOC);
                    setEditModalOpen(true);
                  }}
                >
                  <MdEdit />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setSelectedObservation(obs);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <MdDeleteForever />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Modal de edição */}
        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              p: 4,
              borderRadius: 2,
              width: 400,
              boxShadow: 24,
            }}
          >
            <Typography variant="h6" mb={2}>
              Editar Anotação
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
            />
            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
              <Button onClick={() => setEditModalOpen(false)}>Cancelar</Button>
              <Button variant="contained" onClick={handleEdit}>
                Salvar
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Diálogo de exclusão */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirmar exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir esta anotação?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button color="error" onClick={handleDelete}>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export { NotesUser };
